import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";

// RH-884: pre-select a professional + product for a user who hasn't onboarded.
// Backs the "New user (Onboarding)" tab of the Decorator Links page.

export interface PreselectProduct {
  id: string;
  name: string;
}

export interface PreselectUserProduct extends PreselectProduct {
  onboarded: boolean;
  hasSubmission: boolean;
  // The active pre-selection on the user's onboarding submission (not yet used
  // by a purchase), else null.
  preselection: {
    professionalId: string;
    professionalName: string | null;
    at: string | null;
    source: string | null;
  } | null;
}

export interface PreselectUser {
  id: string;
  full_name: string;
  phone_numbers: string[];
  emails: string[];
  region: string | null;
  products: PreselectUserProduct[];
}

export interface PreselectProfessional {
  id: string;
  name: string;
  picture: string | null;
  type: string | null;
  specialization: string | null;
  years_of_experience: number | null;
  status: string;
  eligible: boolean;
  reason: string | null;
  missingSessions: string[];
}

export interface PreselectProfessionalsResult {
  routed: boolean;
  bundles: { id: string; name: string }[];
  requiredSessions: { id: string; name: string }[];
  professionals: PreselectProfessional[];
}

export type PreselectLinkMode =
  | "new_user"
  | "saved_on_submission"
  | "saved_on_form_submit";

export interface GeneratePreselectLinkResult {
  mode: PreselectLinkMode;
  path: string;
  query: Record<string, string>;
  region: string;
}

export async function getPreselectUser(userId: string): Promise<PreselectUser> {
  const response = await axiosInstance.get(
    `/preselect-link/user/${encodeURIComponent(userId)}`,
  );
  return response.data;
}

// Undo: drop the user's pre-selection for a product → back to round robin.
export async function clearPreselection(
  userId: string,
  productId: string,
): Promise<{ cleared: boolean }> {
  const response = await axiosInstance.delete(
    `/preselect-link/user/${encodeURIComponent(userId)}/product/${encodeURIComponent(productId)}`,
  );
  return response.data;
}

export async function generatePreselectLink(body: {
  product_id: string;
  professional_id: string;
  region: string;
  user_id?: string;
}): Promise<GeneratePreselectLinkResult> {
  const response = await axiosInstance.post("/preselect-link", body);
  return response.data;
}

export function usePreselectProducts(region: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["preselect-link", "products", region],
    enabled: enabled && !!region,
    queryFn: async (): Promise<PreselectProduct[]> => {
      const response = await axiosInstance.get("/preselect-link/products", {
        params: { region },
      });
      return response.data;
    },
  });
}

export function usePreselectProfessionals(params: {
  productId: string | null;
  region: string | undefined;
  userId?: string | null;
}) {
  const { productId, region, userId } = params;
  return useQuery({
    queryKey: ["preselect-link", "professionals", productId, region, userId],
    enabled: !!productId && !!region,
    queryFn: async (): Promise<PreselectProfessionalsResult> => {
      const response = await axiosInstance.get("/preselect-link/professionals", {
        params: {
          product_id: productId,
          region,
          ...(userId ? { user_id: userId } : {}),
        },
      });
      return response.data;
    },
  });
}
